<template>
	<form class="receipts-form" enctype="multipart/form-data" @submit.prevent="submitForm" ref="form">
		<div class="icon">
			<i class="fa-thin fa-receipt"></i>
		</div>
		<errors-list :errors="errors"/>
		<input type="hidden" name="qr_data" :value="qrData">
		<div class="form-row">
			<default-field-box type="text" name="name" :label="$t('forms.receipt_name')" :required="true"
			                   :has-error="errors && errors.name && errors.name.length"
			                   :value="formData.name"/>
		</div>
		<div class="form-row">
			<field-box name="merchant_name" type="text" :label="$t('forms.merchant_name')"
			           :value="formData.merchant_name"/>
		</div>
		<div class="form-row two-columns">
			<field-box name="date" type="date" :label="$t('forms.purchase_date')" :required="true"
			           :has-error="errors && errors.date && errors.date.length"
			           :key="`receipt-date-${formData.date}`"
			           :value="formData.date || null"/>
			<field-box name="currency" type="select" :label="$t('forms.currency')"
			           :options="currencyOptions" :value="formData.currency"/>
		</div>
		<div class="form-row two-columns">
			<field-box name="total_price" type="text" :label="$t('forms.total_price')"
			           :has-error="errors && errors.total_price && errors.total_price.length"
			           :value="formData.total_price"/>
			<field-box name="vat_amount" type="text" :label="$t('forms.vat_amount')"
			           :value="formData.vat_amount"/>
		</div>
		<div class="items-fieldset" v-if="items.length">
			<div class="items-header">
				<small>{{ $t('forms.item_name') }}</small>
				<small>{{ $t('forms.item_price') }}</small>
				<small>{{ $t('forms.item_quantity') }}</small>
				<small>{{ $t('forms.item_vat_rate') }}</small>
			</div>
			<div class="item-row" v-for="(item, index) in items" :key="index">
				<input type="text" :name="`items[${index}][name]`" v-model="item.name" :placeholder="$t('forms.item_name')">
				<input type="number" :name="`items[${index}][price]`" v-model="item.price" step="any" inputmode="decimal" :placeholder="$t('forms.item_price')">
				<input type="number" :name="`items[${index}][quantity]`" v-model="item.quantity" step="any" inputmode="decimal" :placeholder="$t('forms.item_quantity')">
				<input type="number" :name="`items[${index}][vat_rate]`" v-model="item.vat_rate" step="any" inputmode="decimal" :placeholder="$t('forms.item_vat_rate')">
				<button type="button" class="remove-item-btn" @click.stop="removeItem(index)">
					<i class="fa-light fa-trash"></i>
				</button>
			</div>
		</div>
		<div class="form-row">
			<button type="button" class="button style-1" @click="addItem">
				<span class="inner">{{ $t('forms.add_item') }}</span>
			</button>
		</div>
		<div class="form-row qr-preview" v-if="$store.state.app.native && qrData">
				<qr-code-box :text="qrData"/>
			</div>
			<div class="form-row" v-if="$store.state.app.native && ['isanon', 'pluska'].includes($store.state.app.appType)">
				<barcode-field mode="receipt"/>
			</div>
		<dropzone-box name="attachments[]" :label="$t('forms.upload_receipt')" url="api/receipts/attachment" :max-files="0"/>
		<div class="form-actions">
			<button class="button style-4" type="submit" :class="loading ? 'loading' : ''">
				<span class="inner">{{ $t('forms.save') }}</span>
			</button>
			<button class="button style-1" type="button" @click="$store.commit('HIDE_RECEIPTS_CREATE_FORM')">
				<span class="inner">{{ $t('forms.cancel') }}</span>
			</button>
		</div>
	</form>
</template>

<script>
	import ErrorsList from "../containers/ErrorsList";
	import FieldBox from "../form-fields/FieldBox";
	import DefaultFieldBox from "../form-fields/DefaultFieldBox";
	import DropzoneBox from "../form-fields/DropzoneBox";
	import BarcodeField from "../form-fields/BarcodeField";
	import QrCodeBox from "../containers/QrCodeBox";
	import { trackEvent } from '@/analytics';

	export default {
		name: "ReceiptsCreateForm",
		components: {
			ErrorsList,
			FieldBox,
			DefaultFieldBox,
			DropzoneBox,
			BarcodeField,
			QrCodeBox,
		},
		data() {
			const defaultCurrency = this.$store.state.app.appType === 'insanon' ? 'CZK' : 'EUR';
			return {
				loading: false,
				errors: false,
				qrData: '',
				formData: {
					name: '',
					date: null,
					total_price: '',
					currency: defaultCurrency,
					merchant_name: '',
					vat_amount: '',
				},
				items: [],
			}
		},
		computed: {
			currencyOptions() {
				const all = [
					{ value: 'EUR', title: 'EUR' },
					{ value: 'CZK', title: 'CZK' },
					{ value: 'USD', title: 'USD' },
					{ value: 'HUF', title: 'HUF' },
					{ value: 'PLN', title: 'PLN' },
				];
				if (this.$store.state.app.appType === 'insanon') {
					return [all[1], all[0], all[2], all[3], all[4]];
				}
				return all;
			},
		},
		methods: {
			async processQrData(value) {
				this.qrData = value;
				this.$store.commit('SET_APP_LOADING');
				try {
					const response = await this.axios.post('/api/receipts/lookup', { qr_data: value });
					const receipt = response.data.receipt;
					this.formData.name = receipt.merchant_name || '';
					this.formData.date = receipt.date || null;
					this.formData.total_price = receipt.total_price || '';
					this.formData.merchant_name = receipt.merchant_name || '';
					this.formData.vat_amount = receipt.vat_amount || '';
					if (receipt.items && receipt.items.length) {
						this.items = receipt.items.map(i => ({
							name: i.name || '',
							price: i.price || '',
							quantity: i.quantity || 1,
							vat_rate: i.vat_rate || '',
						}));
					}
				} catch {
					this.$swal.fire({
						icon: 'warning',
						title: 'Oops',
						text: this.$t('receipts.lookup_failed'),
					});
				} finally {
					this.$store.commit('UNSET_APP_LOADING');
				}
			},
			addItem() {
				this.items.push({ name: '', price: '', quantity: 1, vat_rate: '' });
			},
			removeItem(index) {
				this.items.splice(index, 1);
			},
			async submitForm() {
				const form = this.$refs.form;
				const formData = new FormData(form);

				this.loading = true;
				this.$store.commit('SET_APP_LOADING');
				trackEvent('form_submit', { form_name: 'receipt_create', module: 'receipts' });
				try {
					const response = await this.axios.post('/api/receipts', formData);
					this._submitSuccess = true;
					trackEvent('form_submit_success', { form_name: 'receipt_create', module: 'receipts' });
					this.$store.commit('ADD_RECEIPT', response.data.receipt);
					this.$store.commit('HIDE_RECEIPTS_CREATE_FORM');
					this.resetForm(form);
				} catch (error) {
					trackEvent('form_submit_error', {
						form_name: 'receipt_create',
						module: 'receipts',
						error_message: error.response && error.response.data ? error.response.data.message : error.message,
						error_status: error.response ? String(error.response.status) : null,
					});
					this.$swal.fire({
						icon: 'error',
						title: 'Oops',
						text: error.response?.data?.message || this.$t('general.default_error'),
					});
					if (error.response?.data?.errors) {
						this.errors = error.response.data.errors;
					}
				} finally {
					this.loading = false;
					this.$store.commit('UNSET_APP_LOADING');
				}
			},
			resetForm(form) {
				form.reset();
				this.formData = { name: '', date: null, total_price: '', currency: this.$store.state.app.appType === 'insanon' ? 'CZK' : 'EUR', merchant_name: '', vat_amount: '' };
				this.items = [];
				this.qrData = '';
				this.errors = false;

				const imageInputs = form.querySelectorAll('input[name="attachments[]"]');
				imageInputs.forEach(el => el.parentNode.removeChild(el));

				const dzPreviews = form.querySelectorAll('.dz-preview');
				dzPreviews.forEach(el => el.parentNode.removeChild(el));

				const dropzone = form.querySelector('.dropzone');
				if (dropzone) dropzone.classList.remove('dz-started');
			},
		},
		mounted() {
			this._submitSuccess = false;
			trackEvent('form_open', { form_name: 'receipt_create', module: 'receipts' });
			this.$root.$on('barcode-scanned', (value) => {
				this.processQrData(value);
			});
		},
		beforeDestroy() {
			if (!this._submitSuccess) {
				trackEvent('form_close', { form_name: 'receipt_create', module: 'receipts' });
			}
		},
		destroyed() {
			this.$root.$off('barcode-scanned');
		}
	}
</script>

<style lang="scss">
.receipts-form {
	.qr-preview {
		display: flex;
		justify-content: center;
	}
}
</style>
<style scoped lang="scss">
	.receipts-form {
		display: flex;
		flex-direction: column;
		flex-grow: 1;
	}

	.icon {
		text-align: center;
		font-size: 21px;
		margin-bottom: 10px;
	}

	.form-row {
		margin-bottom: 15px;

		&:last-child {
			margin-bottom: 0;
		}

		&.two-columns {
			display: flex;
			justify-content: space-between;
			flex-direction: row;

			.field-box {
				width: calc(50% - 8px);
			}
		}
	}

	.items-fieldset {
		margin-bottom: 15px;
	}

	.items-header {
		display: grid;
		grid-template-columns: 2fr 1fr 1fr 1fr 30px;
		gap: 6px;
		margin-bottom: 6px;

		small {
			font-size: 11px;
			opacity: .6;
		}
	}

	.item-row {
		display: grid;
		grid-template-columns: 2fr 1fr 1fr 1fr 30px;
		gap: 6px;
		margin-bottom: 6px;

		input {
			background: rgba(255, 255, 255, .06);
			border: 1px solid rgba(255, 255, 255, .1);
			border-radius: 4px;
			padding: 8px;
			color: inherit;
			font-size: 13px;
			width: 100%;

			&::placeholder {
				font-weight: 400;
			}
		}
	}

	.remove-item-btn {
		background: transparent;
		border: none;
		cursor: pointer;
		color: var(--font-color);
		font-size: 14px;
		padding: 0;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.form-row > .button {
		width: 100%;
	}

	.form-actions {
		margin-top: auto;
		display: flex;
		flex-direction: column;

		.button {
			margin-bottom: 10px;

			&:last-child {
				margin-bottom: 0;
			}
		}
	}

	@media($mobileS) {
		.form-actions {
			margin-top: 25px;
		}
		.form-row {
			margin-bottom: 10px;

			&.two-columns {
				flex-direction: column;

				.field-box {
					width: 100%;
					margin-bottom: 10px;

					&:last-child {
						margin-bottom: 0;
					}
				}
			}
		}
	}
</style>
